import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Landmark, Copy, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Get data from Cart navigation state
  const navState = location.state || {};
  const paymentUrl = navState.paymentUrl || '';
  const total = navState.total || 0;

  // If navigated with orderPlaced=true (cash payment)
  useEffect(() => {
    if (navState.orderPlaced) {
      setOrderPlaced(true);
    }
  }, [navState.orderPlaced]);

  // Check for payment return from CardCom
  useEffect(() => {
    const status = searchParams.get('status');
    const lpId = searchParams.get('lowprofileid') || searchParams.get('LowProfileId');
    
    // If we're inside the CardCom iframe, break out to parent window
    if ((status === 'success' || status === 'failed') && window.self !== window.top) {
      window.top.location.href = window.location.href;
      return;
    }
    
    if (status === 'success') {
      setPaymentStatus('success');
      setOrderPlaced(true);
      clearCart();
      return;
    }
    
    if (status === 'failed') {
      setPaymentStatus('failed');
      return;
    }
    
    if (lpId) {
      checkPaymentStatus(lpId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (lpId) => {
    setPaymentStatus('processing');
    try {
      const response = await axios.post(`${API}/payments/cardcom/result`, {
        low_profile_id: lpId
      });
      
      if (response.data.success) {
        setPaymentStatus('success');
        setOrderPlaced(true);
        clearCart();
        toast.success('התשלום בוצע בהצלחה!');
      } else {
        setPaymentStatus('failed');
        toast.error('התשלום נכשל: ' + (response.data.description || 'שגיאה לא ידועה'));
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('failed');
      toast.error('שגיאה בבדיקת סטטוס התשלום');
    }
  };

  // Auto-redirect to home after successful order
  useEffect(() => {
    if (orderPlaced || paymentStatus === 'success') {
      const delay = navState.fromBankTransfer ? 10000 : 5000;
      const timer = setTimeout(() => {
        navigate('/');
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [orderPlaced, paymentStatus, navigate, navState.fromBankTransfer]);

  // Success screen
  if (orderPlaced || paymentStatus === 'success') {
    return (
      <div className="container py-16" data-testid="order-success">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-14 h-14 text-green-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            ההזמנה התקבלה
          </h2>
          <p className="text-lg text-stone-600 mb-2" style={{ fontFamily: 'Heebo, sans-serif' }}>
            אנחנו נעשה מירב מאמצים להכין לך את ההזמנה בהקדם האפשרי
          </p>
          <p className="text-lg text-stone-600 mb-2" style={{ fontFamily: 'Heebo, sans-serif' }}>
            והמשלוח יגיע אלייך עד 3 ימי עסקים
          </p>
          <p className="text-lg text-stone-600 mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            נתראה בהזמנה הבאה :)
          </p>
          <p className="text-xl font-bold text-[#D97706] mb-10" style={{ fontFamily: 'Heebo, sans-serif' }}>
            תודה מראש
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-button"
            className="h-14 px-10 rounded-full bg-[#D97706] text-white font-bold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            style={{ fontFamily: 'Heebo, sans-serif' }}
          >
            חזרה לדף הבית
          </button>
        </div>
      </div>
    );
  }

  // Failed screen
  if (paymentStatus === 'failed') {
    return (
      <div className="container py-16" data-testid="payment-failed">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            התשלום נכשל
          </h2>
          <p className="text-lg text-stone-600 mb-8">
            אירעה שגיאה בעת עיבוד התשלום. אנא נסה שוב.
          </p>
          <button
            onClick={() => navigate('/cart')}
            data-testid="retry-payment-button"
            className="h-12 px-8 rounded-full bg-[#D97706] text-white font-bold hover:shadow-lg transition-all duration-300"
            style={{ fontFamily: 'Heebo, sans-serif' }}
          >
            חזרה לעגלה
          </button>
        </div>
      </div>
    );
  }

  // Processing screen
  if (paymentStatus === 'processing') {
    return (
      <div className="container py-16" data-testid="payment-processing">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Loader2 className="w-16 h-16 text-[#D97706] animate-spin mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            מעבד תשלום...
          </h2>
          <p className="text-lg text-stone-600">
            אנא המתן בזמן שאנחנו מאמתים את התשלום שלך.
          </p>
        </div>
      </div>
    );
  }

  // Bank transfer details screen
  if (navState.bankTransfer) {
    const copyToClipboard = (text, field) => {
      navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    };

    const bankFields = [
      { label: 'שם הבנק', value: 'דיסקונט', key: 'bank' },
      { label: 'מספר חשבון', value: '154867588', key: 'account' },
      { label: 'סניף', value: '58', key: 'branch' },
      { label: 'על שם', value: 'ברק דדון', key: 'name' },
    ];

    return (
      <div className="container py-12" data-testid="bank-transfer-page">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-stone-100">
            {/* Green header with Discount logo */}
            <div className="bg-gradient-to-br from-[#3aaa5b] to-[#2d8f4a] p-8 flex flex-col items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_d56f6086-1f6c-43f9-a07d-5d56a502df97/artifacts/wot3922n_image.png"
                alt="בנק דיסקונט"
                className="w-24 h-24 rounded-2xl object-cover shadow-lg"
                data-testid="discount-bank-logo"
              />
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Heebo, sans-serif' }}>
                העברה בנקאית
              </h2>
              <div className="bg-white/20 rounded-full px-6 py-2">
                <span className="text-white font-bold text-lg">
                  סכום לתשלום: ₪{total.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Bank details */}
            <div className="p-6 space-y-3" data-testid="bank-details">
              {bankFields.map(({ label, value, key }) => (
                <div 
                  key={key}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-100 hover:bg-stone-100 transition-colors"
                  data-testid={`bank-field-${key}`}
                >
                  <div className="text-right">
                    <p className="text-xs text-stone-500 mb-0.5">{label}</p>
                    <p className="text-lg font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      {value}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(value, key)}
                    className="p-2 rounded-lg hover:bg-white transition-colors text-stone-400 hover:text-[#3aaa5b]"
                    data-testid={`copy-${key}`}
                    title="העתק"
                  >
                    {copiedField === key ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Instructions */}
            <div className="px-6 pb-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-amber-800 font-medium" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  המערכת תזהה את ההזמנה שלך והשם המלא
                </p>
              </div>
              
              <button
                onClick={() => {
                  clearCart();
                  navigate('/checkout', { state: { orderPlaced: true, fromBankTransfer: true } });
                }}
                data-testid="bank-transfer-done-button"
                className="w-full h-14 mt-4 rounded-full bg-[#D97706] text-white font-bold text-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                style={{ fontFamily: 'Heebo, sans-serif' }}
              >
                ביצעתי את ההעברה
              </button>

              <p className="text-center text-[11px] text-stone-500 mt-3 px-2 leading-relaxed" style={{ fontFamily: 'Heebo, sans-serif' }} data-testid="terms-disclaimer-bank">
                בלחיצה על המשך תשלום, קראתי והנני מאשר את{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline font-bold">
                  תנאי השימוש ותקנון האתר
                </a>
              </p>
              
              <button
                onClick={() => navigate('/cart')}
                className="w-full h-10 mt-2 text-stone-500 hover:text-stone-700 transition-colors text-sm"
              >
                חזרה לעגלה
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment iframe view (main purpose of this page now)
  if (paymentUrl) {
    return (
      <div className="container py-8" data-testid="payment-frame-container">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#2C3E2D] to-[#4A5D4B] text-white p-6 text-center">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Heebo, sans-serif' }}>
                תשלום מאובטח
              </h2>
              <p className="text-white/80">
                סכום לתשלום: ₪{total.toFixed(2)}
              </p>
            </div>
            
            <div className="p-4">
              <iframe
                src={paymentUrl}
                className="w-full border-0 rounded-lg"
                style={{ height: '600px', minHeight: '500px' }}
                title="CardCom Payment"
                data-testid="cardcom-iframe"
              />
            </div>
            
            <div className="p-4 border-t border-stone-200 text-center">
              <p className="text-[11px] text-stone-500 mb-3 px-2 leading-relaxed" style={{ fontFamily: 'Heebo, sans-serif' }} data-testid="terms-disclaimer-cardcom">
                בלחיצה על המשך תשלום, קראתי והנני מאשר את{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D97706] hover:underline font-bold">
                  תנאי השימוש ותקנון האתר
                </a>
              </p>
              <button
                onClick={() => navigate('/cart')}
                className="text-stone-500 hover:text-stone-700 underline"
                data-testid="cancel-payment-button"
              >
                ביטול וחזרה לעגלה
              </button>
            </div>
          </div>
          
          <div className="mt-6 text-center text-sm text-stone-500">
            <p>התשלום מאובטח באמצעות CardCom</p>
            <p>כל הפרטים מוצפנים ומאובטחים</p>
          </div>
        </div>
      </div>
    );
  }

  // If someone navigates to /checkout directly with no state, redirect to cart
  return (
    <div className="container py-16" data-testid="checkout-empty">
      <div className="text-center py-16">
        <p className="text-xl text-stone-600 mb-8" style={{ fontFamily: 'Heebo, sans-serif' }}>
          העגלה שלך ריקה
        </p>
        <button
          onClick={() => navigate('/cart')}
          className="h-12 px-8 rounded-full bg-[#D97706] text-white font-bold hover:shadow-lg transition-all duration-300"
          style={{ fontFamily: 'Heebo, sans-serif' }}
        >
          חזרה לעגלה
        </button>
      </div>
    </div>
  );
};

export default Checkout;
